import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, Pencil, Trash2, Users as UsersIcon, X } from 'lucide-react';

const tomGruppe = () => ({
  navn: '', type: 'generell', beskrivelse: '', aktiv: true, farge: '#3b82f6',
});

const VERV = ['medlem', 'leder', 'nestleder', 'kasserer', 'sekretær',
              'styremedlem', 'varamedlem', 'dirigent', 'instruktør', 'trener'];

const TYPER = [
  { v: 'generell',    t: 'Generell' },
  { v: 'styre',       t: 'Styre' },
  { v: 'kor',         t: 'Kor' },
  { v: 'team',        t: 'Team' },
  { v: 'komite',      t: 'Komité' },
  { v: 'aldersgruppe', t: 'Aldersgruppe' },
];

const Grupper = () => {
  const [grupper, setGrupper] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomGruppe());

  // Medlems-håndtering
  const [medlemModalOpen, setMedlemModalOpen] = useState(false);
  const [aktivGruppe, setAktivGruppe] = useState(null);
  const [alleMedlemmer, setAlleMedlemmer] = useState([]);
  const [valgtMedlemId, setValgtMedlemId] = useState('');
  const [valgtVerv, setValgtVerv] = useState('medlem');

  const last = useCallback(() => {
    api.get('/grupper').then((r) => setGrupper(r.data));
  }, []);
  useEffect(() => { last(); }, [last]);

  const aapne = (g) => {
    setRedigerer(g);
    setSkjema(g ? { ...tomGruppe(), ...g } : tomGruppe());
    setOpen(true);
  };
  const lagre = async () => {
    if (!skjema.navn.trim()) { alert('Navn er påkrevd.'); return; }
    if (redigerer) await api.put(`/grupper/${redigerer.id}`, skjema);
    else await api.post('/grupper', skjema);
    setOpen(false); last();
  };
  const slett = async (g) => {
    if (!window.confirm(`Slett gruppe "${g.navn}"? Alle medlemskap fjernes.`)) return;
    await api.delete(`/grupper/${g.id}`); last();
  };

  const aapneMedlemmer = async (g) => {
    const r = await api.get(`/grupper/${g.id}`);
    setAktivGruppe(r.data);
    const m = await api.get('/medlemmer', { params: { status: 'aktiv' } });
    setAlleMedlemmer(m.data);
    setValgtMedlemId('');
    setValgtVerv('medlem');
    setMedlemModalOpen(true);
  };
  const leggTilMedlem = async () => {
    if (!valgtMedlemId) return;
    await api.post(`/grupper/${aktivGruppe.id}/medlemmer`, {
      medlem_id: parseInt(valgtMedlemId, 10), verv: valgtVerv,
    });
    const r = await api.get(`/grupper/${aktivGruppe.id}`);
    setAktivGruppe(r.data);
    setValgtMedlemId('');
    last();
  };
  const fjernMedlem = async (medlem_id) => {
    if (!window.confirm('Fjerne medlem fra gruppen?')) return;
    await api.delete(`/grupper/${aktivGruppe.id}/medlemmer/${medlem_id}`);
    const r = await api.get(`/grupper/${aktivGruppe.id}`);
    setAktivGruppe(r.data);
    last();
  };
  const endreVerv = async (medlem_id, nyttVerv) => {
    await api.put(`/grupper/${aktivGruppe.id}/medlemmer/${medlem_id}`, { verv: nyttVerv });
    const r = await api.get(`/grupper/${aktivGruppe.id}`);
    setAktivGruppe(r.data);
  };

  if (!grupper) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <PageHeader
        title="Grupper & verv"
        actions={
          <button className="btn-primary" onClick={() => aapne(null)}>
            <Plus size={14} /> Ny gruppe
          </button>
        }
      />
      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Navn</th>
              <th>Type</th>
              <th>Antall medlemmer</th>
              <th>Status</th>
              <th style={{ width: 180 }}></th>
            </tr>
          </thead>
          <tbody>
            {grupper.length === 0 && (
              <tr><td colSpan={6} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>
                Ingen grupper opprettet enda.
              </td></tr>
            )}
            {grupper.map((g) => (
              <tr key={g.id}>
                <td><span style={{
                  display: 'inline-block', width: 14, height: 14,
                  borderRadius: 4, background: g.farge,
                }} /></td>
                <td><strong>{g.navn}</strong>
                  {g.beskrivelse && <div className="fm-text-muted" style={{ fontSize: 12 }}>{g.beskrivelse}</div>}
                </td>
                <td>{(TYPER.find(t => t.v === g.type) || {}).t || g.type}</td>
                <td>{g.antall}</td>
                <td>
                  {g.aktiv
                    ? <span className="badge badge-success">Aktiv</span>
                    : <span className="badge badge-warning">Inaktiv</span>}
                </td>
                <td>
                  <button className="btn-ghost" onClick={() => aapneMedlemmer(g)} title="Medlemmer">
                    <UsersIcon size={12} /> Medlemmer
                  </button>
                  <button className="btn-ghost" onClick={() => aapne(g)} title="Rediger"><Pencil size={12} /></button>
                  <button className="btn-ghost" onClick={() => slett(g)} title="Slett"
                          style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Gruppe-modal */}
      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger gruppe' : 'Ny gruppe'}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Navn *</label>
            <input value={skjema.navn} onChange={(e) => setSkjema({ ...skjema, navn: e.target.value })} />
          </div>
          <div>
            <label>Type</label>
            <select value={skjema.type} onChange={(e) => setSkjema({ ...skjema, type: e.target.value })}>
              {TYPER.map(t => <option key={t.v} value={t.v}>{t.t}</option>)}
            </select>
          </div>
          <div>
            <label>Farge</label>
            <input type="color" value={skjema.farge}
              onChange={(e) => setSkjema({ ...skjema, farge: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Beskrivelse</label>
            <textarea rows={3} value={skjema.beskrivelse}
              onChange={(e) => setSkjema({ ...skjema, beskrivelse: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={skjema.aktiv}
                onChange={(e) => setSkjema({ ...skjema, aktiv: e.target.checked })} />
              Gruppe er aktiv
            </label>
          </div>
        </div>
      </Modal>

      {/* Medlemshåndterings-modal */}
      <Modal open={medlemModalOpen} onClose={() => setMedlemModalOpen(false)}
             width={760}
             title={aktivGruppe ? `Medlemmer i ${aktivGruppe.navn}` : 'Medlemmer'}
             footer={<button className="btn-primary" onClick={() => setMedlemModalOpen(false)}>Lukk</button>}>
        {aktivGruppe && (
          <>
            <Card style={{ marginBottom: 12, padding: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '2 1 240px' }}>
                  <label>Legg til medlem</label>
                  <select value={valgtMedlemId} onChange={(e) => setValgtMedlemId(e.target.value)}>
                    <option value="">— velg medlem —</option>
                    {alleMedlemmer
                      .filter(m => !(aktivGruppe.medlemmer || []).some(mg => mg.medlem_id === m.id))
                      .map(m => <option key={m.id} value={m.id}>{m.navn}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label>Verv</label>
                  <select value={valgtVerv} onChange={(e) => setValgtVerv(e.target.value)}>
                    {VERV.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <button className="btn-primary" onClick={leggTilMedlem} disabled={!valgtMedlemId}>
                  <Plus size={14} /> Legg til
                </button>
              </div>
            </Card>

            <table>
              <thead>
                <tr>
                  <th>Navn</th><th>E-post</th><th>Telefon</th><th>Verv</th><th></th>
                </tr>
              </thead>
              <tbody>
                {(aktivGruppe.medlemmer || []).length === 0 && (
                  <tr><td colSpan={5} className="fm-text-muted"
                          style={{ textAlign: 'center', padding: 16 }}>Ingen medlemmer enda.</td></tr>
                )}
                {(aktivGruppe.medlemmer || []).map(mg => (
                  <tr key={mg.id}>
                    <td>{mg.navn}</td>
                    <td>{mg.epost}</td>
                    <td>{mg.telefon}</td>
                    <td>
                      <select value={mg.verv}
                              onChange={(e) => endreVerv(mg.medlem_id, e.target.value)}
                              style={{ padding: '2px 6px' }}>
                        {VERV.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td>
                      <button className="btn-ghost" onClick={() => fjernMedlem(mg.medlem_id)}
                              title="Fjern" style={{ color: 'var(--theme-danger)' }}>
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Modal>
    </>
  );
};

export default Grupper;
